import React, {useEffect, useMemo} from 'react';
import AceEditor from 'react-ace';
import 'ace-builds/src-noconflict/worker-json';
import 'ace-builds/src-noconflict/ace';
import 'ace-builds/src-noconflict/mode-html';
import 'ace-builds/src-noconflict/mode-json';
import 'ace-builds/src-noconflict/theme-chrome';
/*
 * `webpack-resolver` needs to be added in order to load 'worker-json`
 * https://stackoverflow.com/a/65753580/7277750 (comment regarding `webpack-resolver`)
 */
import 'ace-builds/webpack-resolver';
import debounce from 'lodash/debounce';

import './JSONEditor.scss';

interface JSONEditorProps {
  defaultValue: any;
  onChange: (value: string) => void;
  debounceTime?: number;
  disabled?: boolean;
  editorOptions?: any;
  editorProps?: any;
}

const getEditorOptions = (readOnly: boolean = false, editorOptions: any = null) => {
  return {
    minLines: 10,
    maxLines: 15,
    useWorker: true, // JSON worker
    readOnly,
    ...editorOptions || {},
  }
};

const JSONEditorComponent = React.forwardRef(({
  defaultValue = '',
  onChange,
  debounceTime = 800,
  disabled = false,
  editorOptions = undefined,
  editorProps = undefined,
}: JSONEditorProps, ref: any) => {
  useEffect(() => {
    return () => {
      // on component unmount remove debounce for performance reason
      if (debounceOnChange && debounceOnChange.hasOwnProperty('cancel')) {
        debounceOnChange.cancel();
      }
    };
  }, []);

  const _onChange = (value: any) => {
    /*
     * If editor is readOnly, don't trigger change
     */
    if (ref.current?.editor?.getReadOnly()) {
      return;
    }

    onChange(value);
  }

  const debounceOnChange = useMemo(() => debounce(_onChange, debounceTime), [onChange]);

  return (
    <AceEditor
      ref={ref}
      className={'json-editor ' + (disabled ? 'readonly' : '')}
      showPrintMargin={false}
      mode="json"
      theme="chrome"
      height="225px"
      width="100%"
      defaultValue={defaultValue}
      setOptions={getEditorOptions(disabled, editorOptions)}
      onChange={debounceOnChange}
      {...editorProps || {}}
    />
  );
});

export const JSONEditor = React.memo(JSONEditorComponent);
